//-- Copyright (c) 2021 TMT Insights Group, LLC
import { deepCopy } from './utils';
import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { TitleMap } from '@/constants/constants';

export const getTmdbSearchQuery = (title: TitleEntity) => {
  if (!title) return null;
  return title.lineage_meta?.series_name || title.title_name || title.name;
};

export const selectTmdbTitle = (results: TitleEntity[], type?: keyof typeof TitleMap) => {
  if (!results?.length) return null;
  if (Array.isArray(results)) {
    return results.find((r) => !!r.backdrop_path);
  } else {
    const field = type === 'feature' ? 'movie_results' : 'tv_results';
    return results?.[field]?.[0];
  }
};

export const groupFilesbyParentId = (files: any[] = []) => {
  const groupedByParentId: any = {};
  files.reduce((acc, curr) => {
    const field = curr.version_id;
    if (!acc[field]) {
      acc[field] = [];
    }
    acc[field].push(curr);
    return acc;
  }, groupedByParentId);
  return groupedByParentId;
};

export const sortBySequence = (titles: any[]) => {
  if (!titles?.length) return [];
  return deepCopy(titles).sort((a: any, b: any) => {
    if (a.sequence_order === b.sequence_order) {
      return a.name < b.name ? -1 : 1;
    } else {
      return a.sequence_order < b.sequence_order ? -1 : 1;
    }
  });
};

export const sortTitleChildren = (parentTitle: TitleEntity) => {
  const title = deepCopy(parentTitle);
  const sortChildren = (title: TitleEntity) => {
    if (!title.children) return;
    title.children = sortBySequence(title.children);
    title.children.forEach((child: TitleEntity) => sortChildren(child));
    sortChildren(title.children); // TODO: this call looks like a mistake, but I'm not brave enough to remove it. If you know it a mistake please remove it, otherwise fix TS error in this line.
  };
  sortChildren(title);
  return title;
};

export const normalizeTitleData = (titleData: TitleEntity) => {
  const titleDataById = {
    [titleData.id]: { ...titleData, children: sortBySequence(titleData.children) }
  };

  const setChildren = (title: TitleEntity) => {
    if (title.children) {
      sortBySequence(title.children).forEach((child: any) => {
        const id = child.id;
        titleDataById[id] = child;
        setChildren(child);
      });
    }
  };

  setChildren(titleData);
  return titleDataById;
};

/**
 * Function that receives title or version and returns the path (portion of URL containing "/" separated IDs) to the title detail page.
 * This function aimed to provide `path` parameter to the `buildTitleURL` function.
 */
export const getTitlePath = (titleOrVersion: TitleEntity | TitleEntityV2) => {
  // lineage can be readonly (when retrieved from the store), also we don't want to change it, that's why we copy it before reversing.
  // lineage stores title ids in the opposite order compared to the order we use in the title details page URL. That's why we need to reverse it.
  const lineage = [...(titleOrVersion.lineage || [])].reverse();

  if (titleOrVersion.entity_type === 'version') {
    // When provided entity is a version we build path to its parent title solely based on the lineage property
    return lineage;
  }

  const title = titleOrVersion; // at this point we know that passed entity is a title.
  return [...lineage, title.id];
};

export interface BuildTitleURLParams {
  path: (number | string)[];
  tab?: string;
  version?: number | string | 'all';
}

export const buildTitleURL = ({ path, tab = 'metadata', version }: BuildTitleURLParams) => {
  let url = `/titles/${path.join('/')}`;

  const queryParams: string[] = [];
  if (tab) queryParams.push(`tab=${tab}`);
  if (version !== undefined) queryParams.push(`version_id=${version}`);
  if (queryParams.length) {
    url += `?${queryParams.join('&')}`;
  }

  return url;
};